import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { PageLayout, PageHeader, Card, Button, Badge } from '../components/ui'

interface TemplateSummary {
  id: string
  title: string
  description: string
  category: string
  question_count: number
  question_types: string
}

interface TemplateQuestion {
  text: string
  type: string
  required: boolean
  options: string[]
}

interface TemplateDetail extends TemplateSummary {
  questions: TemplateQuestion[]
}

interface Org {
  id: string
  name: string
}

const SurveyTemplatesPage: React.FC = () => {
  const navigate = useNavigate()
  const intl = useIntl()
  useAuth()
  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [organizations, setOrganizations] = useState<Org[]>([])
  const [loading, setLoading] = useState(true)
  const [preview, setPreview] = useState<TemplateDetail | null>(null)
  const [creating, setCreating] = useState<string | null>(null)
  const [selectOrgFor, setSelectOrgFor] = useState<string | null>(null)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token')
    return { 'Authorization': token ? `Bearer ${token}` : '' }
  }

  useEffect(() => {
    const load = async () => {
      try {
        const [templatesRes, orgsRes] = await Promise.all([
          fetch(API_ENDPOINTS.templates.base),
          fetch(API_ENDPOINTS.organizations.base, { headers: getAuthHeaders() }),
        ])
        if (templatesRes.ok) setTemplates(await templatesRes.json())
        if (orgsRes.ok) setOrganizations(await orgsRes.json())
      } catch (err) {
        console.error('Failed to load templates:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleUseTemplate = async (templateId: string, orgId: string) => {
    setCreating(templateId)
    try {
      const response = await fetch(API_ENDPOINTS.templates.createSurvey(templateId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ organization_id: orgId }),
      })
      if (!response.ok) throw new Error('Failed to create survey')
      const survey = await response.json()
      navigate(`/surveys/${survey.id}/share`)
    } catch (err) {
      console.error('Failed to create survey from template:', err)
      alert(intl.formatMessage({ id: 'templates.createSurveyError' }))
    } finally {
      setCreating(null)
      setSelectOrgFor(null)
    }
  }

  const handlePreview = async (templateId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.templates.byId(templateId))
      if (response.ok) setPreview(await response.json())
    } catch (err) {
      console.error('Failed to load template preview:', err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-foreground-muted"><FormattedMessage id="templates.loading" /></p>
        </div>
      </div>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title={intl.formatMessage({ id: 'templates.pageTitle' })}
        subtitle={intl.formatMessage({ id: 'templates.pageSubtitle' })}
        backTo="/dashboard"
        backLabel={intl.formatMessage({ id: 'templates.backToDashboard' })}
      />

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {templates.map((template) => (
          <Card key={template.id} elevated>
            <div className="flex flex-col h-full">
              <div className="mb-3">
                <h3 className="text-lg font-semibold text-foreground">{template.title}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="neutral"><FormattedMessage id="templates.questionCount" values={{ count: template.question_count }} /></Badge>
                  <Badge variant="info">{template.question_types}</Badge>
                </div>
              </div>
              <p className="text-sm text-foreground-muted mb-4 flex-1">{template.description}</p>
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={creating === template.id}
                  onClick={() => {
                    if (organizations.length === 0) {
                      alert(intl.formatMessage({ id: 'templates.createOrgFirstTemplate' }))
                      return
                    }
                    if (organizations.length === 1) {
                      handleUseTemplate(template.id, organizations[0].id)
                    } else {
                      setSelectOrgFor(template.id)
                    }
                  }}
                >
                  {creating === template.id ? <FormattedMessage id="templates.creating" /> : <FormattedMessage id="templates.useTemplate" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreview(template.id)}
                >
                  <FormattedMessage id="templates.preview" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Org Picker Modal */}
      {selectOrgFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4"><FormattedMessage id="templates.selectOrganization" /></h3>
            <div className="space-y-2 mb-4">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  className="w-full text-left px-4 py-3 rounded-lg border border-border-light hover:border-primary-light hover:bg-primary/10 transition-colors"
                  onClick={() => handleUseTemplate(selectOrgFor, org.id)}
                >
                  {org.name}
                </button>
              ))}
            </div>
            <Button variant="ghost" onClick={() => setSelectOrgFor(null)}>
              <FormattedMessage id="templates.cancel" />
            </Button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface border-b border-border-light p-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">{preview.title}</h3>
                  <p className="text-sm text-foreground-muted mt-1"><FormattedMessage id="templates.questionCount" values={{ count: preview.question_count }} /></p>
                </div>
                <Button variant="ghost" onClick={() => setPreview(null)}><FormattedMessage id="templates.close" /></Button>
              </div>
            </div>
            <div className="p-6 pt-4">
              <div className="space-y-3">
                {preview.questions.map((q, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-background rounded-lg">
                    <span className="text-sm font-bold text-foreground-muted mt-0.5">{i + 1}.</span>
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{q.text}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="neutral" size="sm">{q.type}</Badge>
                        {q.required && <Badge variant="warning" size="sm"><FormattedMessage id="templates.required" /></Badge>}
                        {q.options.length > 0 && (
                          <span className="text-xs text-foreground-muted"><FormattedMessage id="templates.optionCount" values={{ count: q.options.length }} /></span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    setPreview(null)
                    if (organizations.length === 1) {
                      handleUseTemplate(preview.id, organizations[0].id)
                    } else if (organizations.length > 1) {
                      setSelectOrgFor(preview.id)
                    } else {
                      alert(intl.formatMessage({ id: 'templates.createOrgFirst' }))
                    }
                  }}
                >
                  <FormattedMessage id="templates.useThisTemplate" />
                </Button>
                <Button variant="ghost" onClick={() => setPreview(null)}><FormattedMessage id="templates.close" /></Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default SurveyTemplatesPage
